import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import DataTable from "react-data-table-component";
import {
    FaPencilAlt,
    FaPlusCircle,
    FaUsers,
    FaFileAlt,
    FaEllipsisV,
    FaTrash,
    FaSearch,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";

import { notifyUser } from "../utils/notifyUser";

const verificationToastContent = (title, message) => (
    <div>
        <h4 className="text-navy font-semibold">{title}</h4>
        <div className="text-sm text-gray-700 mt-1">{message}</div>
    </div>
);

const AdminDashboard = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(null);
    const [, setGroupName] = useState("");
    const [, setComment] = useState("");
    const [searchTerm, setSearchTerm] = useState("");
    const [showActions, setShowActions] = useState(false);
    const [, setShowForm] = useState(false);
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedAuthMethod, setSelectedAuthMethod] = useState("");
    const [selectedRole, setSelectedRole] = useState("");
    const [isPasswordChangeModalOpen, setIsPasswordChangeModalOpen] =
        useState(false);
    const [randomPassword, setRandomPassword] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    // Same custom styles as before

    const filteredConversations = users
        .filter(
            (user) =>
                (user.first_name &&
                    user.first_name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())) ||
                (user.last_name &&
                    user.last_name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())) ||
                (user.email &&
                    user.email
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase())) ||
                (user.role &&
                    user.role.toLowerCase().includes(searchTerm.toLowerCase())),
        )
        .filter((user) => {
            const isOAuth = user.auth_type === "oauth";
            // Filter by auth method
            if (selectedAuthMethod === "oauth") {
                return isOAuth;
            } else if (selectedAuthMethod === "regular") {
                return !isOAuth;
            } else {
                // If no auth filter is selected, continue to next filter
                // No need to return true here as we'll check the role filter next
            }

            // Return true if no auth method filter is applied
            return true;
        })
        .filter((user) => {
            // Filter by role
            if (selectedRole && user.role) {
                return user.role === selectedRole;
            }
            // Return true if no role filter is applied
            return true;
        });
    const totalPages = Math.ceil(filteredConversations.length / perPage);
    const paginatedData = filteredConversations.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage,
    );
    const fromRecord = (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(
        currentPage * perPage,
        filteredConversations.length,
    );
    const totalRecords = filteredConversations.length;

    const actionRef = useRef(null);
    const navigate = useNavigate();

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        };
        return date.toLocaleString("en-US", options);
    };

    const generateRandomPassword = () => {
        const length = 12;
        const charset =
            "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()";
        let password = "";
        for (let i = 0; i < length; i++) {
            // Generate a random character from charset
            password += charset.charAt(
                Math.floor(Math.random() * charset.length),
            );
        }
        // Set the generated password to state
        setRandomPassword(password);
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = () => {
        axios
            .get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users`, {
                withCredentials: true,
            })
            .then((res) => {
                const activeUsers = res.data.filter(
                    (user) => user.is_deleted !== 1,
                );
                //  filter out soft-deleted users

                const usersWithFormattedDates = activeUsers.map((user) => {
                    return {
                        ...user,
                        last_login_at: user.last_login_at
                            ? formatDate(user.last_login_at)
                            : null,
                        last_activity_at: user.last_activity_at
                            ? formatDate(user.last_activity_at)
                            : null,
                    };
                });
                setUsers(sortByAuditUpdatedOn(usersWithFormattedDates));
            })
            .catch((err) => console.error("Error loading users", err));
    };

    const handleExportUsersCSV = () => {
        const rows = filteredConversations.map((u) => ({
            Name: `${u.first_name || ""} ${u.last_name || ""}`.trim(),
            Email: u.email || "",
            Verified: Number(u.verified) === 1 ? "Verified" : "Not Verified",
            Role: u.role || "",
            LastActivity: u.last_activity_at || "",
        }));

        if (!rows.length) {
            notifyUser(null, "info", "No data to export", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) => header.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "users_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleAuthMethodFilter = (e) => {
        setSelectedAuthMethod(e.target.value);
    };

    const handleRoleFilter = (e) => {
        setSelectedRole(e.target.value);
    };

    const handleCreateUser = () => {
        navigate("/create-user");
    };

    const handleSendPasswordEmail = async () => {
        if (!randomPassword) {
            notifyUser(null, "error", "Please generate a password first.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${selectedUser.id}/password-reset-email`,
                {
                    tempPassword: randomPassword,
                },
                { withCredentials: true },
            );
            notifyUser(null, "success", "Password reset email sent successfully!", "toast", null, null, null, null, null, false, false, "top-center");
            setIsPasswordChangeModalOpen(false);
        } catch {
            notifyUser(null, "error", "Error sending password reset email", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleDeleteUser = async () => {
        if (!selectedUser) {
            notifyUser(null, "error", "Please select a user to delete.", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: `You are about to delete user "${selectedUser.first_name} ${selectedUser.last_name}". This action cannot be undone!`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "var(--navy)",
            cancelButtonColor: "#C2D7E2",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                await axios.delete(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${selectedUser.id}`,
                    { withCredentials: true },
                );
                notifyUser(null, "success", "User deleted successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                resetForm();
                fetchUsers();
            } catch (err) {
                console.error("Error deleting user:", err);
                const serverMsg =
                    (err && err.response && (err.response.data && (err.response.data.error || err.response.data.message))) ||
                    err.message ||
                    "Error deleting user";
                notifyUser(null, "error", `Error deleting user: ${serverMsg}`, "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const handleForcePasswordChange = () => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to send the password reset email to the user?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, send email!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.put(
                        `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${selectedUser.id}/force-password-change`,
                        {},
                        { withCredentials: true },
                    );
                    notifyUser(null, "success", "Password reset email sent successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                } catch {
                    notifyUser(null, "error", "Error sending reset email", "toast", null, null, null, null, null, false, false, "top-center");
                }
            }
        });
    };
    const handleToggleLockStatus = async () => {
        if (!selectedUser) return;

        const updatedUser = {
            ...selectedUser,
            is_locked: selectedUser.is_locked === 1 ? 0 : 1,
        };

        try {
            // Send the updated lock status to the backend
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${selectedUser.id}`,
                updatedUser,
                { withCredentials: true },
            );

            // Update the users state with the new lock status
            setUsers(
                users.map((user) =>
                    user.id === selectedUser.id ? updatedUser : user,),
            );

            notifyUser(null, "success", `User ${updatedUser.is_locked === 1 ? "locked" : "unlocked"
                } successfully!`, "toast", null, null, null, null, null, false, false, "top-center");
        } catch {
            notifyUser(null, "error", "Error updating user lock status", "toast", null, null, null, null, null, false, false, "top-center");
        }
    };

    const handleAction = (actionType) => {
        if (!selectedUser) {
            notifyUser(null, "error", "Please select a user first", "toast", null, null, null, null, null, false, false, "top-center");
            return;
        }

        // Define actions for password change and force password change
        if (actionType === "ForcePasswordChange") {
            handleForcePasswordChange();
        } else if (actionType === "PasswordChange") {
            setIsPasswordChangeModalOpen(true);
        } else if (actionType === "ChangeLockStatus") {
            handleToggleLockStatus();
        } else if (actionType === "VerifyUserEmail") {
            handleVerifyUserEmail();
        } else if(actionType === "ResendVerificationEmail") {
            handleResendVerificationEmail();
        } else {
            navigate(`/${actionType.toLowerCase()}/${selectedUser.id}`);
        }
    };

    const handleVerifyUserEmail = async () => {
        if (!selectedUser) return;

        if (selectedUser.verified === 1) {
            notifyUser(null, "info", `${selectedUser.email} is already verified.`, "toast", null, null, null, null, null, false, false, "top-center");
            setShowActions(false);
            return;
        }

        const result = await Swal.fire({
            title: "Are you sure?",
            text: `Do you want to manually verify the email for "${selectedUser.email}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "var(--navy)",
            cancelButtonColor: "#C2D7E2",
            confirmButtonText: "Yes, verify it!",
            cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
            try {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${selectedUser.id}/verify-email`,
                    {},
                    { withCredentials: true },
                );
                notifyUser(null, "success", "User email verified successfully!", "toast", null, null, null, null, null, false, false, "top-center");
                setSelectedUser({ ...selectedUser, verified: 1 });
                fetchUsers();
            } catch (err) {
                if (err?.response?.data?.alreadyVerified) {
                    notifyUser(null, "info", `${selectedUser.email} is already verified.`, "toast", null, null, null, null, null, false, false, "top-center");
                } else {
                    notifyUser(null, "error", "Error verifying user email", "toast", null, null, null, null, null, false, false, "top-center");
                }
            }
        }
        setShowActions(false);
    };

    const handleResendVerificationEmail = async() => {
        if(!selectedUser || Number(selectedUser.verified) === 1) return;

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${selectedUser.id}/resend-verification`,
                {},
                { withCredentials: true },
            );
            notifyUser(
                null,
                "success",
                verificationToastContent(
                    "Verification Email Sent",
                    response.data.message,
                ),
                "toast",
                null,
                null,
                8000,
                null,
                null,
                false,
                false,
                "top-center",
            );
            fetchUsers();
        } catch(error) {
            const message =
                error.response?.data?.error ||
                "Unable to resend the verification email.";
            notifyUser(
                null,
                "error",
                verificationToastContent(
                    "Unable to Send Verification Email",
                    message,
                ),
                "toast",
                null,
                null,
                8000,
                null,
                null,
                false,
                false,
                "top-center",
            );
        }
        setShowActions(false);
    };

    const resetForm = () => {
        setSelectedUser(null);
        setGroupName("");
        setComment("");
        setShowForm(false);
    };

    const toggleActions = () => setShowActions((prev) => !prev);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                actionRef.current &&
                !actionRef.current.contains(event.target)
            ) {
                setShowActions(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const customStyles = {
        table: {
            style: {
                borderRadius: "8px",
                overflow: "hidden",
            },
        },
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "14px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "13px",
            },
        },
        rows: {
            style: {
                cursor: "pointer",
                minHeight: "45px",
                "&:hover": {
                    backgroundColor: "#f1f8ff",
                },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    const columns = [
        {
            name: "Name",
            selector: (row) => {
                // If both first and last name are null/empty, show signature text for investors or signature_name for entrepreneurs
                if (!row.first_name && !row.last_name) {
                    if (row.role === "investor" && row.signature_text) {
                        return row.signature_text;
                    }
                    if (row.role === "entrepreneur" && row.signature_name) {
                        return row.signature_name;
                    }
                }
                return `${row.first_name || ""} ${row.last_name || ""}`.trim();
            },
            sortable: true,
        },
        {
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
        },
        {
            name: "Verified",
            selector: (row) =>
                Number(row.verified) === 1 ? "Verified" : "Not Verified",
            cell: (row) => {
                const isVerified = Number(row.verified) === 1;

                return (
                    <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
                            isVerified
                                ? "bg-green-100 text-green-700"
                                : "bg-red-100 text-red-700"
                        }`}
                    >
                        {isVerified ? "Verified" : "Not Verified"}
                    </span>
                );
            },
            sortable: true,
        },
        {
            name: "Role",
            selector: (row) => row.role,
            sortable: true,
        },

        {
            name: "Last Activity",
            selector: (row) => {
                if (!row.last_activity_at) {
                    return "-";
                }
                return formatDate(row.last_activity_at);
            },
            sortable: true,
        },
    ];

    // Handle the change of any field in the user editing modal
    const handleChange = (e) => {
        setSelectedUser({
            ...selectedUser,
            [e.target.name]: e.target.value,
        });
    };

    // Handle updating the user details (save changes)
    const handleUpdate = async () => {
        if (selectedUser) {
            try {
                await axios.put(
                    `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/users/${selectedUser.id}`,
                    selectedUser,
                    { withCredentials: true },
                );
                setUsers(
                    users.map((user) =>
                        user.id === selectedUser.id ? selectedUser : user,),
                );
                setIsEditModalOpen(false);
                notifyUser(null, "success", "User updated successfully!", "toast", null, null, null, null, null, false, false, "top-center");
            } catch {
                notifyUser(null, "error", "Error updating user", "toast", null, null, null, null, null, false, false, "top-center");
            }
        }
    };

    const handleEdit = (user) => {
        setSelectedUser(user);
        setIsEditModalOpen(true);
    };
    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {filteredConversations.length === 0 ? 0 : fromRecord} to{" "}
                {toRecord} of {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1}
                    onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                    }
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );
    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                minHeight: "80vh",
                height: "auto",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
                overflowX: "hidden",
            }}
        >
            <h2
                style={{
                    color: "var(--navy)",
                    marginBottom: "-30px",
                    fontSize: "1rem",
                    fontFamily: "large",
                }}
            >
                USERS
            </h2>
            <div className="container mx-auto p-5">
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "20px",
                        gap: "10%",
                    }}
                ></div>

                <div
                    className="aqi-grid-toolbar"
                    style={{
                        display: "flex",
                        flexWrap: "nowrap",
                        alignItems: "center",
                        gap: "8px",
                        overflow: "visible",
                    }}
                >
                    <div>
                        <select
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230B1F3A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            value={perPage}
                            onChange={(e) => {
                                setPerPage(Number(e.target.value));
                                setCurrentPage(1);
                            }}
                        >
                            {GRID_CONFIG.pageSizeOptions.map((option) => (
                                <option key={option} value={option}>{option}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <select
                            value={selectedAuthMethod}
                            onChange={handleAuthMethodFilter}
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230B1F3A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                                width: "clamp(105px, 11vw, 150px)",
                                minWidth: "100px",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        >
                            <option value="">All Login Types</option>
                            <option value="regular">Regular Login</option>
                            <option value="oauth">OAuth Logins</option>
                        </select>
                    </div>

                    <div>
                        <select
                            value={selectedRole}
                            onChange={handleRoleFilter}
                            style={{
                                padding: "0.375rem 2rem 0.375rem 0.75rem",
                                color: "var(--navy)",
                                backgroundColor: "#F5F7FA",
                                border: "2px solid #C2D7E2",
                                borderRadius: "0.375rem",
                                fontWeight: "500",
                                appearance: "none",
                                backgroundImage:
                                    "url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230B1F3A' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                                backgroundRepeat: "no-repeat",
                                backgroundPosition: "right 0.5rem center",
                                backgroundSize: "16px 12px",
                                transition: "all 0.2s ease",
                                cursor: "pointer",
                                outline: "none",
                                width: "clamp(90px, 9vw, 130px)",
                                minWidth: "85px",
                            }}
                            className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        >
                            <option value="">All Roles</option>
                            <option value="investor">Investor</option>
                            <option value="entrepreneur">Entrepreneur</option>
                            <option value="admin">Admin</option>
                        </select>
                    </div>

                    <div
                        className="aqi-grid-search"
                        style={{
                            width: "clamp(120px, 15vw, 240px)",
                            minWidth: "110px",
                            flexShrink: 1,
                        }}
                    >
                        <FaSearch
                            className="absolute left-3 top-2.5"
                            style={{ color: "var(--navy)" }}
                        />
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={searchTerm}
                            onChange={handleSearch}
                            style={{
                                paddingLeft: "2.5rem",
                                paddingRight: "0.75rem",
                                borderRadius: "0.375rem",
                                border: "2px solid #C2D7E2",
                                backgroundColor: "#F5F7FA",
                                transition: "all 0.2s ease",
                                outline: "none",
                                color: "var(--navy)",
                                width: "100%"
                            }}
                            className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                        />
                    </div>

                    <div
                        className="aqi-grid-toolbar-actions aqi-grid-toolbar-actions--export-last"
                        style={{
                            display: "flex",
                            flexWrap: "nowrap",
                            alignItems: "center",
                            gap: "6px",
                            flexShrink: 0,
                            whiteSpace: "nowrap",
                        }}
                    >
                        <button
                            className="aqi-grid-button aqi-grid-button--primary"
                            style={{ flexShrink: 0 }}
                            onClick={handleCreateUser}
                        >
                            <FaPlusCircle size={16} />
                            Create
                        </button>
                        {selectedUser && (
                            <>
                                <button
                                    className="aqi-grid-button aqi-grid-button--primary"
                                    style={{ flexShrink: 0 }}
                                    onClick={() => handleEdit(selectedUser)}
                                >
                                    <FaPencilAlt size={16} />
                                    Edit
                                </button>

                                <button
                                    className="aqi-grid-button aqi-grid-button--danger"
                                    style={{ flexShrink: 0 }}
                                    onClick={handleDeleteUser}
                                >
                                    <FaTrash size={16} />
                                    Delete
                                </button>

                                <div className="relative z-50" ref={actionRef}>
                                    <button
                                        className="aqi-grid-button aqi-grid-button--warning"
                                        style={{ flexShrink: 0 }}
                                        onClick={toggleActions}
                                    >
                                        <FaEllipsisV size={16} />
                                        Actions
                                    </button>

                                    {showActions && (
                                        <div className="absolute z-50 right-0 mt-2 w-64 bg-white border border-gray-300 rounded shadow-lg ">
                                        <button
                                            className="flex items-center w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                            onClick={() =>
                                                handleAction(
                                                    "ForcePasswordChange",
                                                )
                                            }
                                        >
                                            <FaUsers
                                                size={16}
                                                className="mr-2"
                                            />
                                            Force password change
                                        </button>
                                        <button
                                            className="flex items-center w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                            onClick={() =>
                                                handleAction("PasswordChange")
                                            }
                                        >
                                            <FaFileAlt
                                                size={16}
                                                className="mr-2"
                                            />
                                            Change User&apos;s password
                                        </button>
                                        <button
                                            className="flex items-center w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                            onClick={() =>
                                                handleAction("ChangeLockStatus")
                                            }
                                        >
                                            <FaFileAlt
                                                size={16}
                                                className="mr-2"
                                            />
                                            Change Lock Status
                                        </button>
                                        <button
                                            className="flex items-center w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                            onClick={() =>
                                                handleAction("VerifyUserEmail")
                                            }
                                        >
                                            <FaFileAlt
                                                size={16}
                                                className="mr-2"
                                            />
                                            Verify User Email
                                        </button>
                                        {Number(selectedUser.verified) !== 1 && (
                                            <button
                                                className="flex items-center w-full text-left px-3 py-2 text-sm hover:bg-gray-100"
                                                onClick={() =>
                                                    handleAction("ResendVerificationEmail")
                                                }
                                            >
                                                <FaFileAlt
                                                    size={16}
                                                    className="mr-2"
                                                />
                                                Resend Verification Email
                                            </button>
                                        )}
                                    </div>
                                    )}
                                </div>
                            </>
                        )}
                        <button
                            onClick={handleExportUsersCSV}
                            title="Export CSV"
                            className="aqi-grid-button aqi-grid-button--primary"
                            style={{ flexShrink: 0 }}
                        >
                            Export
                        </button>
                    </div>
                </div>

                <div style={{ overflowX: "auto", width: "100%" }}>
                    <DataTable
                        columns={columns}
                        data={paginatedData}
                        highlightOnHover
                        pointerOnHover
                        pagination={false}
                        onRowClicked={(row) => setSelectedUser(row)}
                        responsive
                        striped
                        paginationContainerStyles={{
                            display: "none",
                        }}
                        subHeaderWrap={false}
                        customStyles={customStyles}
                        conditionalRowStyles={[
                            {
                                when: (row) =>
                                    selectedUser && selectedUser.id === row.id,
                                style: {
                                    backgroundColor: "#DDDDDD",
                                    color: "black",
                                    fontWeight: "bold",
                                    fontSize: "0.9rem",
                                },
                            },
                            {
                                // Check if is_locked is true (locked)
                                when: (row) => row.is_locked === 1,
                                style: {
                                    backgroundColor: "#f8d7da",
                                    color: "black",
                                    fontWeight: "bold",
                                },
                            },
                        ]}
                    />
                </div>

                <CustomPagination />

                {/* Modal for Editing User */}
                {isEditModalOpen && selectedUser && (
                    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white p-8 rounded-lg shadow-lg w-96 max-h-[90vh] overflow-y-auto">
                            <h2 className="text-xl font-semibold mb-4">
                                Edit User
                            </h2>
                            <div>
                                <label className="block mb-2">First Name</label>
                                <input
                                    type="text"
                                    name="first_name"
                                    value={selectedUser.first_name || ""}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>
                            <div className="mt-4">
                                <label className="block mb-2">Last Name</label>
                                <input
                                    type="text"
                                    name="last_name"
                                    value={selectedUser.last_name || ""}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>
                            <div className="mt-4">
                                <label className="block mb-2">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={selectedUser.email || ""}
                                    onChange={handleChange}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>
                            <div className="mt-4">
                                <label className="block mb-2">Role</label>
                                <select
                                    name="role"
                                    value={selectedUser.role || ""}
                                    onChange={handleChange}
                                    className="w-full p-2 border rounded"
                                    required
                                >
                                    <option value="" disabled selected>
                                        Select Role
                                    </option>
                                    <option value="user">User</option>
                                    <option value="admin">Admin</option>
                                </select>
                            </div>
                            <div className="mt-4 flex justify-between">
                                <button
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="bg-gray-300 text-white px-4 py-2 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdate}
                                    className="aqi-primary-action rounded bg-blue-500 px-4 py-2 text-white"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal for Password Change */}
                {isPasswordChangeModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                        <div className="bg-white p-6 rounded-2xl shadow-2xl w-[90%] sm:w-[400px] relative">
                            {/* Optional Close Button */}
                            <button
                                onClick={() =>
                                    setIsPasswordChangeModalOpen(false)
                                }
                                className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 text-xl"
                            >
                                &times;
                            </button>

                            <h2 className="text-xl font-semibold text-center mb-6 text-gray-800">
                                Change User&apos;s Password
                            </h2>

                            <div className="text-center mb-6">
                                <button
                                    onClick={generateRandomPassword}
                                    className="bg-black hover:bg-gray-800 text-white font-semibold px-4 py-2 rounded-md shadow-sm transition"
                                >
                                    Generate Random Password
                                </button>
                            </div>

                            {randomPassword && (
                                <div className="mb-6">
                                    <label className="block mb-1 text-sm text-gray-700 font-medium">
                                        Generated Password
                                    </label>
                                    <input
                                        type="text"
                                        value={randomPassword}
                                        readOnly
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-sm"
                                    />
                                </div>
                            )}

                            <div className="flex justify-between gap-3">
                                <button
                                    onClick={() =>
                                        setIsPasswordChangeModalOpen(false)
                                    }
                                    className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-md font-medium transition"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleSendPasswordEmail}
                                    className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-md font-medium transition"
                                >
                                    Send Reset Email
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminDashboard;
